import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  Input,
  LoadingState,
  Toast,
  IconArrowLeft,
  IconSettings,
} from "../ui";
import { buildQrPreviewUrl, buildTournamentPublicUrl, isPremiumPlan } from "./premiumMediaUtils";
import { useConfirm } from "../common/ConfirmDialog";
import PosterCanvasEditor from "./PosterCanvasEditor";
import {
  clampLayout,
  createBlankCanvasLayout,
  createDefaultCanvasLayout,
  parseCanvasLayoutJson,
} from "./posterCanvasDefaults";
import { buildPosterPdfFileName, exportPosterElementToPdf } from "./posterPdfExport";
import useTournamentPosterData from "./useTournamentPosterData";

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function previewModeFromLayout(layout) {
  const blocks = layout?.blocks || [];
  const w = blocks.find((b) => b.type === "widget");
  const k = w?.widgetKind;
  if (k === "matches" || k === "bracket" || k === "groups" || k === "schedule") return k;
  const iframe = blocks.find((b) => b.type === "iframe");
  const m = iframe?.iframeMode;
  if (m === "matches" || m === "bracket" || m === "groups") return m;
  return "groups";
}

/** Normalizes a poster-design summary (camel or Pascal case) into a stable shape. */
function normalizeDesignSummary(d) {
  return {
    id: d?.id ?? d?.Id ?? null,
    name: d?.name ?? d?.Name ?? "",
    previewMode: d?.previewMode ?? d?.PreviewMode ?? "",
    updatedAtUtc: d?.updatedAtUtc ?? d?.UpdatedAtUtc ?? null,
  };
}

/** Human label for a design in the switcher (falls back when unnamed). */
function designLabel(summary, index) {
  const name = (summary?.name ?? "").trim();
  if (name) return name;
  return `Cartel ${index + 1}`;
}

function applyPosterDesignPayload(design, tournamentNameFallback, setters) {
  const {
    setPosterTitle,
    setPosterSubtitle,
    setPublicUrl,
    setQrSize,
    setSponsors,
    setCanvasLayout,
    setSelectedBlockId,
    setHasSavedDesignOnServer,
    setLastSavedAtUtc,
    setActiveDesignId,
    setDesignName,
  } = setters;

  const idVal = design?.id ?? design?.Id ?? null;
  setActiveDesignId?.(idVal);

  const nameVal = design?.name ?? design?.Name;
  setDesignName?.(typeof nameVal === "string" ? nameVal : "");

  const posterTitleVal = design?.posterTitle ?? design?.PosterTitle;
  if (typeof posterTitleVal === "string" && posterTitleVal.trim()) {
    setPosterTitle(posterTitleVal);
  } else {
    setPosterTitle(`Cartel oficial - ${tournamentNameFallback}`);
  }

  const posterSubtitleVal = design?.posterSubtitle ?? design?.PosterSubtitle;
  if (typeof posterSubtitleVal === "string" && posterSubtitleVal.trim()) {
    setPosterSubtitle(posterSubtitleVal);
  }

  const publicUrlVal = design?.publicUrl ?? design?.PublicUrl;
  if (typeof publicUrlVal === "string" && publicUrlVal.trim()) {
    setPublicUrl(publicUrlVal);
  }

  const qr = design?.qrSize ?? design?.QrSize;
  if (Number.isFinite(Number(qr))) {
    setQrSize(Number(qr));
  }

  const imgs = design?.sponsorImages ?? design?.SponsorImages;
  if (Array.isArray(imgs)) {
    setSponsors(
      imgs.filter((x) => typeof x === "string" && x.trim().length > 0).slice(0, 6)
    );
  } else {
    setSponsors([]);
  }

  const rawLayout = design?.canvasLayoutJson ?? design?.CanvasLayoutJson;
  const layoutStr = typeof rawLayout === "string" ? rawLayout : null;
  const parsed = parseCanvasLayoutJson(layoutStr);
  if (parsed) {
    setCanvasLayout(clampLayout(parsed));
  } else {
    setCanvasLayout(createDefaultCanvasLayout());
  }

  setSelectedBlockId(null);

  const saved = design?.isSaved ?? design?.IsSaved;
  setHasSavedDesignOnServer(Boolean(saved));

  const updated = design?.updatedAtUtc ?? design?.UpdatedAtUtc;
  setLastSavedAtUtc(typeof updated === "string" ? updated : updated != null ? String(updated) : null);
}

export default function TournamentPremiumMediaPage() {
  const { tournamentId } = useParams();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveInfo, setSaveInfo] = useState("");

  const [tournamentName, setTournamentName] = useState("Torneo");
  const [tournamentSlug, setTournamentSlug] = useState("");
  const [canManage, setCanManage] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);

  const [posterTitle, setPosterTitle] = useState("");
  const [posterSubtitle, setPosterSubtitle] = useState(
    "Diseno premium para publicar en redes, imprimir o mostrar en sala."
  );
  const [publicUrl, setPublicUrl] = useState("");
  const [qrSize, setQrSize] = useState(260);
  const [sponsors, setSponsors] = useState([]);
  const [uploadingSponsors, setUploadingSponsors] = useState(false);
  const [savingDesign, setSavingDesign] = useState(false);

  const [canvasLayout, setCanvasLayout] = useState(() => createDefaultCanvasLayout());
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [hasSavedDesignOnServer, setHasSavedDesignOnServer] = useState(false);
  const [lastSavedAtUtc, setLastSavedAtUtc] = useState(null);
  const [reloadingDesign, setReloadingDesign] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Multiple stored designs per tournament.
  const [designs, setDesigns] = useState([]);
  const [activeDesignId, setActiveDesignId] = useState(null);
  const [designName, setDesignName] = useState("");
  const [designBusy, setDesignBusy] = useState(false);

  const posterData = useTournamentPosterData(tournamentId);

  useEffect(() => {
    const onBefore = () => setPrinting(true);
    const onAfter = () => setPrinting(false);
    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      setSaveInfo("");
      try {
        const [accessRes, tournamentRes, premiumRes] = await Promise.all([
          axios.get(`${appConstants.API_URL}/api/tournaments/${tournamentId}/access`),
          axios.get(`${appConstants.API_URL}/api/tournaments/${tournamentId}`),
          axios.get(`${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/status`),
        ]);

        if (cancelled) return;

        const name =
          tournamentRes.data?.name || tournamentRes.data?.Name || "Torneo";
        const slug = tournamentRes.data?.slug ?? tournamentRes.data?.Slug ?? "";
        setTournamentName(name);
        setTournamentSlug(typeof slug === "string" ? slug : "");
        setPosterTitle(`Cartel oficial - ${name}`);
        setCanManage(Boolean(accessRes.data?.canManage));
        setPremiumStatus(premiumRes.data ?? null);

        const origin =
          typeof window !== "undefined" ? window.location.origin : "";
        // Prefer slug for a readable QR URL; fall back to id.
        setPublicUrl(buildTournamentPublicUrl(origin, { slug, id: tournamentId }));

        try {
          const listRes = await axios.get(
            `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs`
          );
          if (cancelled) return;

          const list = Array.isArray(listRes.data) ? listRes.data.map(normalizeDesignSummary) : [];
          setDesigns(list);

          if (list.length > 0 && list[0].id) {
            const designRes = await axios.get(
              `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs/${list[0].id}`
            );
            if (cancelled) return;
            applyPosterDesignPayload(designRes.data ?? {}, name, {
              setPosterTitle,
              setPosterSubtitle,
              setPublicUrl,
              setQrSize,
              setSponsors,
              setCanvasLayout,
              setSelectedBlockId,
              setHasSavedDesignOnServer,
              setLastSavedAtUtc,
              setActiveDesignId,
              setDesignName,
            });
          } else {
            // No saved designs yet: start a fresh template (unsaved).
            setActiveDesignId(null);
            setDesignName("");
            setCanvasLayout(createDefaultCanvasLayout());
            setHasSavedDesignOnServer(false);
            setLastSavedAtUtc(null);
          }
        } catch {
          if (!cancelled) {
            setDesigns([]);
            setActiveDesignId(null);
            setDesignName("");
            setCanvasLayout(createDefaultCanvasLayout());
            setHasSavedDesignOnServer(false);
            setLastSavedAtUtc(null);
          }
        }
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 403) {
          setLoadError("No tienes permisos para acceder al estudio premium de este torneo.");
        } else if (error?.response?.status === 404) {
          setLoadError("No se encontro el torneo solicitado.");
        } else {
          setLoadError("No se pudo cargar el estudio premium.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  useEffect(() => {
    setCanvasLayout((prev) => {
      const next = { ...prev, blocks: [...prev.blocks] };
      let changed = false;
      next.blocks = next.blocks.filter((b) => {
        if (b.type !== "sponsor") return true;
        if (sponsors.length === 0) {
          changed = true;
          return false;
        }
        if (b.sponsorIndex >= sponsors.length) {
          changed = true;
          return false;
        }
        return true;
      });
      next.blocks = next.blocks.map((b) => {
        if (b.type === "sponsor" && sponsors.length > 0) {
          const idx = Math.min(b.sponsorIndex ?? 0, sponsors.length - 1);
          if (idx !== b.sponsorIndex) {
            changed = true;
            return { ...b, sponsorIndex: idx };
          }
        }
        return b;
      });
      return changed ? next : prev;
    });
  }, [sponsors]);

  const qrPreviewUrl = buildQrPreviewUrl(publicUrl, qrSize);
  const premiumEnabled = Boolean(premiumStatus?.premiumEnabled);
  const premiumPlan = premiumStatus?.plan ?? "Free";
  const planLooksPremium = isPremiumPlan(premiumPlan);
  const enabledFeatures = useMemo(
    () => new Set(Array.isArray(premiumStatus?.enabledFeatures) ? premiumStatus.enabledFeatures : []),
    [premiumStatus]
  );
  const canDesignPoster = premiumEnabled && enabledFeatures.has("poster_designer");
  const canUseSponsors = premiumEnabled && enabledFeatures.has("custom_sponsor_images");
  const canUseQr = premiumEnabled && enabledFeatures.has("tournament_qr_codes");

  const buildDesignPayload = useCallback(
    (nameValue) => ({
      name: nameValue && nameValue.trim() ? nameValue.trim() : null,
      posterTitle: posterTitle || null,
      posterSubtitle: posterSubtitle || null,
      previewMode: previewModeFromLayout(canvasLayout),
      publicUrl: canUseQr ? publicUrl || null : null,
      qrSize: canUseQr ? Number(qrSize || 260) : null,
      sponsorImages: canUseSponsors ? sponsors : [],
      canvasLayout,
    }),
    [posterTitle, posterSubtitle, canvasLayout, canUseQr, publicUrl, qrSize, canUseSponsors, sponsors]
  );

  const refreshDesignList = useCallback(async () => {
    try {
      const res = await axios.get(
        `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs`
      );
      setDesigns(Array.isArray(res.data) ? res.data.map(normalizeDesignSummary) : []);
    } catch {
      /* non-fatal: keep the current list */
    }
  }, [tournamentId]);

  const handleSaveDesign = useCallback(async () => {
    if (!canDesignPoster) return;

    setSavingDesign(true);
    setSaveInfo("");
    try {
      const payload = buildDesignPayload(designName);
      const base = `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs`;
      const res = activeDesignId
        ? await axios.put(`${base}/${activeDesignId}`, payload)
        : await axios.post(base, payload);

      const saved = res.data ?? {};
      const savedId = saved.id ?? saved.Id ?? activeDesignId;
      if (savedId) setActiveDesignId(savedId);
      const savedName = saved.name ?? saved.Name;
      if (typeof savedName === "string") setDesignName(savedName);
      setHasSavedDesignOnServer(true);
      const upd = saved.updatedAtUtc ?? saved.UpdatedAtUtc;
      if (upd != null) setLastSavedAtUtc(String(upd));
      await refreshDesignList();
      if (typeof upd === "string" && upd) {
        setSaveInfo(`Diseno guardado (${new Date(upd).toLocaleString("es-ES")}).`);
      } else {
        setSaveInfo("Diseno guardado correctamente.");
      }
    } catch (error) {
      setSaveInfo(
        error?.response?.data?.message || "No se pudo guardar la carteleria premium."
      );
    } finally {
      setSavingDesign(false);
    }
  }, [
    canDesignPoster,
    buildDesignPayload,
    designName,
    tournamentId,
    activeDesignId,
    refreshDesignList,
  ]);

  const handleSponsorUpload = async (event) => {
    if (!canUseSponsors) {
      setSaveInfo("Tu plan actual no incluye patrocinadores personalizados.");
      event.target.value = "";
      return;
    }

    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    const availableSlots = Math.max(0, 6 - sponsors.length);
    if (availableSlots <= 0) {
      setSaveInfo("Ya tienes 6 imagenes de patrocinio cargadas.");
      return;
    }

    setUploadingSponsors(true);
    setSaveInfo("");
    try {
      const limited = files.slice(0, availableSlots);
      const dataUrls = await Promise.all(limited.map(readFileAsDataUrl));
      setSponsors((prev) => [...prev, ...dataUrls.filter(Boolean)]);
      if (files.length > limited.length) {
        setSaveInfo("Solo se han anadido las primeras imagenes hasta completar 6.");
      }
    } catch {
      setSaveInfo("No se pudieron leer una o mas imagenes.");
    } finally {
      setUploadingSponsors(false);
      event.target.value = "";
    }
  };

  const removeSponsor = (index) => {
    setSponsors((prev) => prev.filter((_, i) => i !== index));
  };

  const editorProps = {
    layout: canvasLayout,
    onLayoutChange: setCanvasLayout,
    tournamentId,
    posterTitle,
    posterSubtitle,
    sponsors,
    qrPreviewUrl,
    publicUrl,
    qrSize,
    canUseQr,
    canUseSponsors,
    selectedBlockId,
    onSelectBlock: setSelectedBlockId,
    onUserHint: (msg) => setSaveInfo(msg),
    posterData,
  };

  const studioOpen = canManage && premiumEnabled;

  const designSetters = useMemo(
    () => ({
      setPosterTitle,
      setPosterSubtitle,
      setPublicUrl,
      setQrSize,
      setSponsors,
      setCanvasLayout,
      setSelectedBlockId,
      setHasSavedDesignOnServer,
      setLastSavedAtUtc,
      setActiveDesignId,
      setDesignName,
    }),
    []
  );

  const loadDesignById = useCallback(
    async (id) => {
      const designRes = await axios.get(
        `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs/${id}`
      );
      applyPosterDesignPayload(designRes.data ?? {}, tournamentName, designSetters);
    },
    [tournamentId, tournamentName, designSetters]
  );

  const handleReloadSavedDesign = useCallback(async () => {
    if (!canDesignPoster || !activeDesignId) return;
    const ok = await confirm({
      title: "Recargar diseño guardado",
      message:
        "Se descartarán los cambios que no hayas guardado y se cargará la última versión guardada en el servidor.",
      confirmText: "Recargar",
    });
    if (!ok) return;

    setReloadingDesign(true);
    setSaveInfo("");
    try {
      await loadDesignById(activeDesignId);
      setSaveInfo("Diseno recargado desde el servidor.");
    } catch (error) {
      setSaveInfo(
        error?.response?.data?.message || "No se pudo recargar el diseno guardado."
      );
    } finally {
      setReloadingDesign(false);
    }
  }, [canDesignPoster, activeDesignId, confirm, loadDesignById]);

  const handleSelectDesign = useCallback(
    async (id) => {
      if (!id || id === activeDesignId) return;
      const ok = await confirm({
        title: "Cambiar de diseño",
        message: "Se descartarán los cambios que no hayas guardado del diseño actual.",
        confirmText: "Cambiar",
      });
      if (!ok) return;

      setDesignBusy(true);
      setSaveInfo("");
      try {
        await loadDesignById(id);
      } catch (error) {
        setSaveInfo(
          error?.response?.data?.message || "No se pudo cargar el diseño seleccionado."
        );
      } finally {
        setDesignBusy(false);
      }
    },
    [activeDesignId, confirm, loadDesignById]
  );

  const handleNewDesign = useCallback(
    async (kind) => {
      if (!canDesignPoster) return;
      const ok = await confirm({
        title: "Nuevo diseño",
        message:
          "Se descartarán los cambios no guardados del diseño actual y empezarás uno nuevo (sin guardar).",
        confirmText: "Crear nuevo",
      });
      if (!ok) return;

      setActiveDesignId(null);
      setDesignName("");
      setPosterTitle(`Cartel oficial - ${tournamentName}`);
      setPosterSubtitle("Diseno premium para publicar en redes, imprimir o mostrar en sala.");
      setSponsors([]);
      setSelectedBlockId(null);
      setHasSavedDesignOnServer(false);
      setLastSavedAtUtc(null);
      setCanvasLayout(kind === "blank" ? createBlankCanvasLayout() : createDefaultCanvasLayout());
      setSaveInfo("Nuevo diseño sin guardar. Pulsa Guardar diseno para almacenarlo.");
    },
    [canDesignPoster, confirm, tournamentName]
  );

  const handleDuplicateDesign = useCallback(async () => {
    if (!canDesignPoster) return;
    setDesignBusy(true);
    setSaveInfo("");
    try {
      const base = (designName && designName.trim()) || "Cartel";
      const copyName = `${base} (copia)`.slice(0, 120);
      const payload = buildDesignPayload(copyName);
      const res = await axios.post(
        `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs`,
        payload
      );
      const saved = res.data ?? {};
      const newId = saved.id ?? saved.Id ?? null;
      if (newId) setActiveDesignId(newId);
      setDesignName(saved.name ?? saved.Name ?? copyName);
      setHasSavedDesignOnServer(true);
      const upd = saved.updatedAtUtc ?? saved.UpdatedAtUtc;
      if (upd != null) setLastSavedAtUtc(String(upd));
      await refreshDesignList();
      setSaveInfo("Diseño duplicado: ahora editas la copia.");
    } catch (error) {
      setSaveInfo(error?.response?.data?.message || "No se pudo duplicar el diseño.");
    } finally {
      setDesignBusy(false);
    }
  }, [canDesignPoster, designName, buildDesignPayload, tournamentId, refreshDesignList]);

  const handleDeleteDesign = useCallback(async () => {
    if (!canDesignPoster || !activeDesignId) return;
    const ok = await confirm({
      title: "Eliminar diseño",
      message: "Esta acción no se puede deshacer. ¿Eliminar este diseño guardado?",
      confirmText: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;

    setDesignBusy(true);
    setSaveInfo("");
    try {
      const deletedId = activeDesignId;
      await axios.delete(
        `${appConstants.API_URL}/api/tournaments/${tournamentId}/premium/poster-designs/${deletedId}`
      );
      const remaining = designs.filter((d) => d.id !== deletedId);
      setDesigns(remaining);
      if (remaining.length > 0 && remaining[0].id) {
        await loadDesignById(remaining[0].id);
      } else {
        setActiveDesignId(null);
        setDesignName("");
        setCanvasLayout(createDefaultCanvasLayout());
        setSelectedBlockId(null);
        setHasSavedDesignOnServer(false);
        setLastSavedAtUtc(null);
      }
      setSaveInfo("Diseño eliminado.");
    } catch (error) {
      setSaveInfo(error?.response?.data?.message || "No se pudo eliminar el diseño.");
    } finally {
      setDesignBusy(false);
    }
  }, [canDesignPoster, activeDesignId, confirm, tournamentId, designs, loadDesignById]);

  const handleExportPdf = useCallback(async () => {
    if (!canDesignPoster || pdfBusy) return;
    if (posterData.loading) {
      setSaveInfo("Espera a que terminen de cargar los datos del torneo antes de generar el PDF.");
      return;
    }
    setPdfBusy(true);
    setSaveInfo("");
    setPrinting(true);
    // Let React flush the print-mode state, then wait for fonts and two frames
    // so html2canvas captures the final layout (no "Cargando…" text).
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    try {
      if (document.fonts && typeof document.fonts.ready?.then === "function") {
        await document.fonts.ready;
      }
    } catch {
      /* ignore */
    }
    await new Promise((r) => setTimeout(r, 80));
    try {
      const el = document.getElementById("poster-pdf-capture-root");
      const name = buildPosterPdfFileName(tournamentName, tournamentId);
      await exportPosterElementToPdf(el, name);
      setSaveInfo(
        "PDF listo: deberia abrirse en una pestana nueva. Desde el visor de PDF usa Imprimir o Archivo / Guardar."
      );
    } catch (err) {
      setSaveInfo(err?.message || "No se pudo generar el PDF. Prueba de nuevo o reduce el tamano del lienzo.");
    } finally {
      setPrinting(false);
      setPdfBusy(false);
    }
  }, [canDesignPoster, pdfBusy, posterData.loading, tournamentId, tournamentName]);

  const savedStatusLine = useMemo(() => {
    if (!hasSavedDesignOnServer) {
      return "Aun no hay un diseno guardado en el servidor para este torneo. Pulsa Guardar diseno para persistirlo.";
    }
    if (lastSavedAtUtc) {
      try {
        const d = new Date(lastSavedAtUtc);
        return `Ultima version en servidor: ${d.toLocaleString("es-ES")}.`;
      } catch {
        return "Hay un diseno guardado en el servidor.";
      }
    }
    return "Hay un diseno guardado en el servidor.";
  }, [hasSavedDesignOnServer, lastSavedAtUtc]);

  if (loading) {
    return <LoadingState label="Cargando estudio premium..." className="min-h-[45vh]" />;
  }

  return (
    <div
      className={
        studioOpen
          ? "flex min-h-[100dvh] flex-col bg-neutral-100"
          : "ui-page-wrap py-4 sm:py-6"
      }
    >
      <style>
        {`@media print {
          .premium-studio-topbar { display: none !important; }
          .premium-controls { display: none !important; }
          #premium-poster-preview { box-shadow: none !important; border: none !important; padding: 0 !important; }
          body { background: #fff !important; }
        }`}
      </style>

      <header className="premium-studio-topbar sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b border-neutral-300 bg-white/95 px-3 py-2 shadow-sm backdrop-blur-sm print:hidden sm:gap-3 sm:px-4">
        <Button as={RouterLink} to={`/tournamentactions/${tournamentId}`} variant="outline" size="sm">
          <IconArrowLeft size={16} />
          Volver al torneo
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-base font-extrabold text-ink sm:text-lg">
            Estudio Premium — {tournamentName}
          </h1>
          {studioOpen ? (
            <p className="hidden text-[11px] text-ink-muted sm:block">
              Tablas ligeras (grupos, partidos, bracket). Arrastra desde la barra gris; redimensiona por bordes y esquinas.
            </p>
          ) : null}
        </div>
        {studioOpen ? (
          <Button
            variant="gold"
            size="sm"
            onClick={handleExportPdf}
            disabled={!canDesignPoster || pdfBusy || posterData.loading}
            title={posterData.loading ? "Cargando datos del torneo…" : undefined}
          >
            {pdfBusy
              ? "Generando PDF..."
              : posterData.loading
              ? "Cargando datos…"
              : "Ver PDF / imprimir"}
          </Button>
        ) : null}
      </header>

      <div className={studioOpen ? "flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-2 sm:px-3" : ""}>
        {loadError ? (
          <Toast kind="error" className={studioOpen ? "mt-2" : "mt-6"}>
            {loadError}
          </Toast>
        ) : null}

        {saveInfo ? (
          <Toast kind="info" className="mt-2">
            {saveInfo}
          </Toast>
        ) : null}

        {!canManage && !loadError ? (
          <Toast kind="warning" className="mt-4">
            Esta seccion solo esta disponible para gestores del torneo.
          </Toast>
        ) : null}

        {canManage && !premiumEnabled ? (
        <div className="mt-8">
          <EmptyState
            title="Feature premium bloqueada"
            description={`Tu plan actual es ${premiumPlan}. Activa Pro o Enterprise para usar carteles personalizados y QR.`}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Badge variant={planLooksPremium ? "warning" : "neutral"}>
                  Plan actual: {premiumPlan}
                </Badge>
                <Button as={RouterLink} to={`/tournamentactions/${tournamentId}/setup`} variant="outline">
                  <IconSettings size={16} />
                  Revisar configuracion
                </Button>
              </div>
            }
          />
        </div>
      ) : null}

      {canManage && premiumEnabled ? (
        <div className="grid min-h-0 flex-1 gap-4 overflow-hidden lg:grid-cols-[minmax(260px,300px)_1fr] xl:grid-cols-[minmax(280px,320px)_1fr]">
          <Card className="premium-controls h-fit">
            <CardHeader>
              <h2 className="font-display text-xl font-extrabold text-ink">Texto y datos</h2>
              <p className="mt-1 text-sm text-ink-muted">
                Cabecera, QR y logos. El lienzo esta a la derecha.
              </p>
              <p className="mt-2 rounded-lg border border-arena/15 bg-cream/50 p-2 text-xs leading-snug text-ink-muted">
                <span className="font-semibold text-ink-soft">Varios carteles por torneo.</span> Crea diseños distintos
                (p. ej. uno con calendario + QR y otro con equipos + QR), guárdalos con un nombre y cámbialos cuando
                quieras. <span className="font-semibold text-ink-soft">Guardar diseno</span> actualiza el cartel activo;
                usa <span className="font-semibold text-ink-soft">Nuevo</span> o{" "}
                <span className="font-semibold text-ink-soft">Duplicar</span> para crear otro.
              </p>
              <p className="mt-2 text-xs text-ink-muted">{savedStatusLine}</p>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="rounded-lg border border-arena/15 bg-white/60 p-3">
                <p className="mb-2 text-sm font-semibold text-ink-soft">
                  Mis diseños {designs.length > 0 ? `(${designs.length})` : ""}
                </p>
                {designs.length > 0 ? (
                  <select
                    className="focus-ring mb-2 w-full rounded-xl border border-arena/20 bg-white px-3 py-2 text-sm"
                    value={activeDesignId ?? ""}
                    disabled={designBusy}
                    onChange={(event) => handleSelectDesign(event.target.value)}
                  >
                    {!activeDesignId ? (
                      <option value="">Diseño nuevo (sin guardar)</option>
                    ) : null}
                    {designs.map((d, i) => (
                      <option key={d.id} value={d.id}>
                        {designLabel(d, i)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mb-2 text-xs text-ink-muted">
                    Aún no hay diseños guardados. Guarda este para empezar tu colección.
                  </p>
                )}
                <Input
                  label="Nombre del diseño"
                  value={designName}
                  placeholder="Ej. Calendario + QR"
                  onChange={(event) => setDesignName(event.target.value)}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleNewDesign("template")}
                    disabled={!canDesignPoster || designBusy}
                  >
                    Nuevo (plantilla)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleNewDesign("blank")}
                    disabled={!canDesignPoster || designBusy}
                  >
                    Nuevo (en blanco)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDuplicateDesign}
                    disabled={!canDesignPoster || designBusy}
                  >
                    Duplicar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteDesign}
                    disabled={!canDesignPoster || designBusy || !activeDesignId}
                  >
                    Eliminar
                  </Button>
                </div>
              </div>

              <Input
                label="Titulo"
                value={posterTitle}
                onChange={(event) => setPosterTitle(event.target.value)}
              />
              <Input
                label="Subtitulo"
                value={posterSubtitle}
                onChange={(event) => setPosterSubtitle(event.target.value)}
              />
              <Input
                label="URL publica para QR"
                value={publicUrl}
                onChange={(event) => setPublicUrl(event.target.value)}
              />
              <Input
                label="Tamano QR (px)"
                type="number"
                value={qrSize}
                min={120}
                max={800}
                onChange={(event) => setQrSize(Number(event.target.value || 260))}
              />

              <div>
                <p className="mb-2 text-sm font-semibold text-ink-soft">
                  Patrocinadores (maximo 6)
                </p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSponsorUpload}
                  disabled={!canUseSponsors || uploadingSponsors || sponsors.length >= 6}
                  className="focus-ring w-full rounded-xl border border-arena/20 bg-white px-3 py-2 text-sm"
                />
                <p className="mt-2 text-xs text-ink-muted">
                  {canUseSponsors
                    ? "Anade bloques Banner / logo en el lienzo para colocar cada imagen."
                    : "Tu plan no incluye carga de patrocinadores personalizados."}
                </p>
              </div>

              {!canUseSponsors ? null : sponsors.length === 0 ? (
                <p className="text-xs text-ink-muted">Aun no hay imagenes cargadas.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {sponsors.map((src, index) => (
                    <div
                      key={index}
                      className="relative overflow-hidden rounded-lg border border-arena/12 bg-white p-1"
                    >
                      <img
                        src={src}
                        alt={`Sponsor ${index + 1}`}
                        className="h-14 w-full rounded object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => removeSponsor(index)}
                        className="focus-ring absolute right-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white"
                        aria-label={`Eliminar sponsor ${index + 1}`}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
            <CardFooter className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleReloadSavedDesign}
                disabled={!canDesignPoster || reloadingDesign}
              >
                {reloadingDesign ? "Recargando..." : "Recargar guardado"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setSponsors([])}
                disabled={sponsors.length === 0}
              >
                Limpiar sponsors
              </Button>
              <Button
                variant="outline"
                onClick={() => setPublicUrl(buildTournamentPublicUrl(window.location.origin, { slug: tournamentSlug, id: tournamentId }))}
              >
                Reset URL
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setCanvasLayout(createDefaultCanvasLayout());
                  setSelectedBlockId(null);
                  setSaveInfo("Lienzo restablecido a la plantilla inicial.");
                }}
              >
                Plantilla inicial
              </Button>
              <Button
                variant="gold"
                onClick={handleSaveDesign}
                disabled={!canDesignPoster || savingDesign}
              >
                {savingDesign ? "Guardando..." : "Guardar diseno"}
              </Button>
            </CardFooter>
          </Card>

          <div
            id="premium-poster-preview"
            className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-neutral-300 bg-white p-2 shadow-sm sm:p-3"
          >
            <PosterCanvasEditor {...editorProps} interactive={!printing} />
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}
